import * as React from "react";
import { BarChart, Box, Button } from "@amzn/awsui-components-react";
import { UATProject, UATStatus } from "../../types/UATProject";
import { useMemo } from "react";

interface TestRunsViewProps {
  project: UATProject;
}

export default function TestStatusHistoryView({ project }: TestRunsViewProps) {
  const { seriesData, dates } = useMemo(() => {
    // Get all unique dates
    const allDates = project.testCases
        .flatMap(tc => tc.history)
        .map(h => new Date(h.runDate).toISOString().split('T')[0]);

    const uniqueDates = [...new Set(allDates)]
        .map(dateStr => new Date(dateStr))
        .sort((a, b) => a.getTime() - b.getTime());

    // Get unique statuses
    const uniqueStatuses = [...new Set(
        project.testCases
            .flatMap(tc => tc.history)
            .map(h => h.status)
    )];

    const statusCounts = uniqueStatuses.map(status => ({
      title: String(status),
      type: "bar" as const,
      data: uniqueDates.map(date => {
        const dateStr = date.toISOString().split('T')[0];

        // Count test cases with this status on this date
        const count = project.testCases.reduce((acc, testCase) => {
          // Find the latest status for this test case up to this date
          const latestRun = testCase.history
              .filter(h => new Date(h.runDate).toISOString().split('T')[0] <= dateStr)
              .sort((a, b) => new Date(b.runDate).getTime() - new Date(a.runDate).getTime())[0];

          // If we found a run and its status matches, increment the counter
          return acc + (latestRun?.status === status ? 1 : 0);
        }, 0);

        return {
          x: date,
          y: count
        };
      })
    }));

    return {
      seriesData: statusCounts,
      dates: uniqueDates
    };
  }, [project.testCases]);


  return (
      <BarChart<Date>
          series={seriesData}
          xDomain={dates}
          // detailPopoverSeriesContent={(detail: any) => (
          //     <div>
          //       <div>Status: {detail.series.title}</div>
          //       <div>Count: {detail.datum.y}</div>
          //       <div>Date: {detail.datum.x.toLocaleDateString()}</div>
          //     </div>
          // )}
          ariaLabel="Test Status History"
          detailPopoverFooter={(xValue: Date) => {
            const totalTests = seriesData.reduce((sum, series) => {
              const dataPoint = series.data.find(d =>
                  d.x.toISOString().split('T')[0] === xValue.toISOString().split('T')[0]
              );
              return sum + (dataPoint?.y || 0);
            }, 0);

            return (
                <>
                  <hr />
                  <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between"
                      }}
                  >
                    <span>Total Test Cases</span>
                    <span>{totalTests}</span>
                  </div>
                </>
            );
          }}
          height={150}
          hideFilter
          stackedBars
          xTickFormatter={(d: Date) =>
              d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })
          }
          yTickFormatter={(d: number) => Math.round(d).toString()}
          xTitle="Test Run Date"
          yTitle="Number of Test Cases"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data available</b>
              <Box variant="p" color="inherit">
                There is no test history data available
              </Box>
            </Box>
          }
          noMatch={
            <Box textAlign="center" color="inherit">
              <b>No matching data</b>
              <Box variant="p" color="inherit">
                There is no matching test data to display
              </Box>
              <Button>Clear filter</Button>
            </Box>
          }
      />
  );
}
